import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CalendarDays, Clapperboard, GraduationCap, ArrowUpRight, ArrowLeft, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import TalkForm from "@/components/site/TalkForm";
import { Reveal } from "@/components/site/Motion";
import { openBroby } from "@/components/site/BrobyChat";
import { useLang } from "@/lib/i18n";

const EXPERIENCES = [
  { key: "singelijn", link: "https://archive.cvb.be/fr/films/a-ma-place" },
  { key: "autre", link: "https://ccf.brussels/lecole-inclusive-lecole-de-tous-les-possibles-un-film-de-matilda-ancora/" },
  { key: "vents", link: "https://www.rtbf.be/article/la-difficile-scolarisation-des-enfants-autistes-9232859" },
  { key: "gamp", link: "https://archive.cvb.be/fr/films/a-ma-place" },
];

const DOCS = [
  { n: 1, link: "https://www.rtbf.be/article/l-ecole-inclusive-l-ecole-de-tous-les-possibles-10704716" },
  { n: 2, link: "https://www.mammouth.media/pas-si-differents/" },
  { n: 3, link: "https://archive.cvb.be/fr/films/a-ma-place" },
  { n: 4, link: "https://www.rtbf.be/article/documentaire-eclaireuses-une-pedagogie-hors-des-sentiers-battus-10981997" },
  { n: 5, link: "https://www.rtbf.be/article/la-difficile-scolarisation-des-enfants-autistes-9232859" },
];

export default function Inclusion() {
  const { t, lang } = useLang();
  const [talkOpen, setTalkOpen] = useState(false);
  const [fascicules, setFascicules] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fascicules`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setFascicules(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const latest = fascicules[0];
  const older = fascicules.slice(1);

  const shareFascicule = async () => {
    const title = lang === "nl" ? latest.title_nl : latest.title;
    const url = `${window.location.origin}/inclusion`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `PAZAPA — ${title}`, text: title, url });
      } catch {
        /* annulé par l'utilisateur */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("incl.booklets.copied"));
    } catch {
      /* ignore */
    }
  };

  return (
    <main className="relative" data-testid="inclusion-page">
      <Header onTalk={() => setTalkOpen(true)} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute top-24 left-[8%] w-3 h-3 rounded-full dot-yellow" />
          <div className="absolute top-44 right-[10%] w-2 h-2 rounded-full dot-coral" />
          <div className="absolute bottom-16 left-[40%] w-2.5 h-2.5 rounded-full dot-sage" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <Link
              to="/"
              data-testid="inclusion-back-home"
              className="inline-flex items-center gap-2 text-sm font-semibold text-foreground/60 hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
              {t("nav.home")}
            </Link>
            <div className="mt-6 chip">{t("incl.chip")}</div>
            <h1 className="mt-5 font-heading text-4xl md:text-6xl leading-[1.05] max-w-3xl">
              {t("incl.title_1")} <span className="text-primary">{t("incl.title_accent")}</span>
            </h1>
            <p className="mt-6 text-lg text-foreground/75 leading-relaxed max-w-2xl">{t("incl.subtitle")}</p>
            <p className="mt-4 text-xs text-foreground/55 italic">{t("incl.note")}</p>
          </Reveal>
        </div>
      </section>

      {/* Expériences bruxelloises */}
      <section className="py-14 md:py-20 bg-muted/50" data-testid="inclusion-experiences">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <div className="chip">{t("incl.exp.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] max-w-2xl">{t("incl.exp.title")}</h2>
            <p className="mt-4 text-lg text-foreground/75 leading-relaxed max-w-2xl">{t("incl.exp.subtitle")}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {EXPERIENCES.map((e, i) => (
              <Reveal key={e.key} delay={i * 0.06}>
                <article
                  className="rounded-3xl bg-white border border-border p-6 md:p-7 h-full flex flex-col hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(23,49,58,0.25)] transition-all"
                  data-testid={`inclusion-exp-${e.key}`}
                >
                  <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: "hsl(var(--sky))", color: "hsl(var(--primary))" }}>
                    <GraduationCap className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-4 font-heading text-xl leading-tight">{t(`incl.exp.${e.key}.title`)}</h3>
                  <p className="mt-2 text-base text-foreground/75 leading-relaxed flex-1">{t(`incl.exp.${e.key}.text`)}</p>
                  <a
                    href={e.link}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`inclusion-exp-link-${e.key}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary link-underline self-start"
                  >
                    {t("incl.exp.link")}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Documentaires */}
      <section className="py-14 md:py-20" data-testid="inclusion-docs">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <div className="chip">{t("incl.docs.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] max-w-2xl">{t("incl.docs.title")}</h2>
            <p className="mt-4 text-lg text-foreground/75 leading-relaxed max-w-2xl">{t("incl.docs.subtitle")}</p>
          </Reveal>
          <div className="mt-10 space-y-4">
            {DOCS.map((d, i) => (
              <Reveal key={d.n} delay={i * 0.05}>
                <a
                  href={d.link}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`inclusion-doc-${d.n}`}
                  className="group flex items-start gap-4 md:gap-6 rounded-3xl bg-white border border-border p-5 md:p-6 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-18px_rgba(23,49,58,0.3)] transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: "#FFE5DE", color: "hsl(var(--coral))" }}>
                    <Clapperboard className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg md:text-xl leading-tight group-hover:text-primary transition-colors">
                      {t(`incl.docs.${d.n}.title`)}
                    </h3>
                    <div className="mt-1 text-xs font-bold uppercase tracking-widest text-foreground/55">
                      {t(`incl.docs.${d.n}.meta`)}
                    </div>
                    <p className="mt-2 text-sm md:text-base text-foreground/75 leading-relaxed">{t(`incl.docs.${d.n}.text`)}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 mt-1 shrink-0 text-foreground/40 group-hover:text-primary transition-colors" strokeWidth={2.5} />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Repères / événements */}
      <section className="py-14 md:py-20 bg-muted/50" data-testid="inclusion-events">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <div className="chip">{t("incl.events.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] max-w-2xl">{t("incl.events.title")}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[1, 2].map((n, i) => (
              <Reveal key={n} delay={i * 0.06}>
                <article className="rounded-3xl bg-white border border-border p-6 md:p-7 h-full" data-testid={`inclusion-event-${n}`}>
                  <div className="w-12 h-12 rounded-2xl grid place-items-center" style={{ backgroundColor: "#FFF3C4", color: "hsl(var(--foreground))" }}>
                    <CalendarDays className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-4 font-heading text-xl leading-tight">{t(`incl.events.${n}.title`)}</h3>
                  <p className="mt-2 text-base text-foreground/75 leading-relaxed">{t(`incl.events.${n}.text`)}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-6 text-sm text-foreground/60 italic max-w-2xl">{t("incl.events.note")}</p>
          </Reveal>
        </div>
      </section>

      {/* Fascicule du mois */}
      <section className="py-14 md:py-20" data-testid="inclusion-booklets">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <div className="chip">{t("incl.booklets.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05] max-w-2xl">{t("incl.booklets.title")}</h2>
            <p className="mt-4 text-lg text-foreground/75 leading-relaxed max-w-2xl">{t("incl.booklets.subtitle")}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-stretch">
            {latest && (
              <Reveal>
                <article
                  id="fascicule-print"
                  className="rounded-3xl p-6 md:p-8 h-full border-2"
                  style={{ backgroundColor: "hsl(var(--sky))", borderColor: "hsl(var(--primary) / 0.25)" }}
                  data-testid="inclusion-booklet-latest"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-white text-primary">
                    <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} />
                    {t("incl.booklets.edition")}
                    {latest.edition} — {lang === "nl" ? latest.month_label_nl : latest.month_label}
                  </div>
                  <h3 className="mt-4 font-heading text-2xl leading-tight">
                    {lang === "nl" ? latest.title_nl : latest.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {(lang === "nl" ? latest.points_nl : latest.points).map((p, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-base text-foreground/85 leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "hsl(var(--primary))" }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex flex-wrap gap-2 print:hidden">
                    <button
                      onClick={() => window.print()}
                      data-testid="booklet-print-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-sm font-bold border border-primary/30 hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      <Printer className="w-4 h-4" strokeWidth={2.4} />
                      {t("incl.booklets.print")}
                    </button>
                    <button
                      onClick={shareFascicule}
                      data-testid="booklet-share-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary text-sm font-bold border border-primary/30 hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      <Share2 className="w-4 h-4" strokeWidth={2.4} />
                      {t("incl.booklets.share")}
                    </button>
                  </div>
                </article>
              </Reveal>
            )}
            <Reveal delay={0.08}>
              <article
                className="rounded-3xl p-6 md:p-8 h-full bg-white border border-dashed border-border grid place-items-center text-center"
                data-testid="inclusion-booklet-next"
              >
                <div>
                  <div className="w-12 h-12 mx-auto rounded-2xl grid place-items-center bg-muted text-foreground/50">
                    <BookOpen className="w-6 h-6" strokeWidth={2.2} />
                  </div>
                  <h3 className="mt-4 font-heading text-xl text-foreground/70">{t("incl.booklets.next")}</h3>
                  <p className="mt-2 text-sm text-foreground/55">{t("incl.booklets.next_text")}</p>
                </div>
              </article>
            </Reveal>
          </div>

          {older.length > 0 && (
            <div className="mt-12">
              <Reveal>
                <h3 className="font-heading text-2xl md:text-3xl">{t("incl.booklets.archive")}</h3>
              </Reveal>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {older.map((f, idx) => (
                  <Reveal key={f.edition} delay={idx * 0.05}>
                    <article
                      className="rounded-3xl bg-white border border-border p-6 md:p-7 h-full"
                      data-testid={`inclusion-booklet-${f.edition}`}
                    >
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-muted text-foreground/70">
                        <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} />
                        {t("incl.booklets.edition")}
                        {f.edition} — {lang === "nl" ? f.month_label_nl : f.month_label}
                      </div>
                      <h4 className="mt-3 font-heading text-xl leading-tight">
                        {lang === "nl" ? f.title_nl : f.title}
                      </h4>
                      <ul className="mt-3 space-y-2">
                        {(lang === "nl" ? f.points_nl : f.points).map((p, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/75 leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "hsl(var(--primary))" }} />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-muted/50" data-testid="inclusion-cta">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <Reveal>
            <div className="rounded-3xl bg-foreground text-background p-8 md:p-12 text-center">
              <h2 className="font-heading text-3xl md:text-4xl leading-tight max-w-2xl mx-auto">{t("incl.cta.title")}</h2>
              <p className="mt-4 text-base md:text-lg text-background/75 max-w-xl mx-auto">{t("incl.cta.text")}</p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <button
                  onClick={openBroby}
                  data-testid="inclusion-cta-broby"
                  className="pill-btn bg-white text-foreground hover:bg-background/90"
                >
                  {t("incl.cta.broby")}
                </button>
                <button
                  onClick={() => setTalkOpen(true)}
                  data-testid="inclusion-cta-talk"
                  className="pill-btn border-2 border-background/40 text-background hover:bg-background/10"
                >
                  {t("incl.cta.talk")}
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
      <TalkForm open={talkOpen} onClose={() => setTalkOpen(false)} />
    </main>
  );
}
